import React from 'react';
import {
  Typography,
  Box,
  useTheme,
  Divider,
  Link,
} from '@mui/material';
import { PageLayout } from '../../components/footer/PageLayout';
import { ContentSection } from '../../components/footer/ContentSection';

export const PrivacyPage: React.FC = () => {
  const theme = useTheme();

  return (
    <PageLayout
      title="Privacy Policy"
      subtitle="Your privacy is important to us. This policy explains how we collect, use, and protect your personal information."
    >
      <ContentSection>
        <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
          <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', fontStyle: 'italic' }}>
            Last Updated: December 12, 2025
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            At Careva, we are committed to protecting your privacy and ensuring the security of your personal information. 
            This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you use our 
            platform to connect with healthcare providers and caregivers.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 1: Information We Collect */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.main }}>
            1. Information We Collect
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: '600', mb: 2, mt: 3 }}>
            1.1 Account Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            When you create an account, we collect:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Email address (required for account creation)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>First and last name</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Profile photo (optional)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Phone number (optional, for contact purposes)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>User role (Patient, Caregiver, or Admin)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Firebase Authentication UID (for secure authentication)</Typography></li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: '600', mb: 2, mt: 3 }}>
            1.2 Profile Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            For caregivers, we collect additional profile information:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Professional bio and background</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Location information</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Years of experience</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Specialties and areas of expertise</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Professional certifications and licenses</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Services offered and pricing</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Availability schedule</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Languages spoken</Typography></li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: '600', mb: 2, mt: 3 }}>
            1.3 Health Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            For patients, we may collect health-related information:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Age (optional)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Health conditions (voluntarily provided)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Care needs and requirements</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Emergency contact information</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Appointment notes and special requirements</Typography></li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: '600', mb: 2, mt: 3 }}>
            1.4 Appointment Information
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Scheduled appointment dates and times</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Service details and duration</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Appointment notes and special requirements</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Payment status and transaction information</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Appointment history and status</Typography></li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: '600', mb: 2, mt: 3 }}>
            1.5 Training and Certification Data
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Training course progress and completion status</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Quiz attempts and scores</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Training certificates and credentials</Typography></li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: '600', mb: 2, mt: 3 }}>
            1.6 Communication Data
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Messages sent and received through our platform</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Conversation history</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Reviews and ratings you provide or receive</Typography></li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: '600', mb: 2, mt: 3 }}>
            1.7 Technical Information
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Device tokens for push notifications</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>IP address and browser information</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Usage data and analytics</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Cookies and similar tracking technologies</Typography></li>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 2: How We Use Your Information */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.main }}>
            2. How We Use Your Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            We use the information we collect to:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Provide, maintain, and improve our services</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Facilitate connections between patients and caregivers</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Process and manage appointments</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Enable communication between users</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Verify caregiver credentials and certifications</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Provide training and certification programs</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Send you important updates, notifications, and service-related communications</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Respond to your inquiries and provide customer support</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Detect, prevent, and address technical issues and security threats</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Comply with legal obligations and enforce our terms of service</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Analyze usage patterns to improve user experience</Typography></li>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 3: Information Sharing */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.main }}>
            3. Information Sharing and Disclosure
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: '600', mb: 2, mt: 3 }}>
            3.1 With Other Users
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            To facilitate connections, we share:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Caregiver profiles (including name, bio, specialties, certifications) with patients searching for care</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Patient information (name, contact details) with caregivers for appointment coordination</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Appointment details with both parties involved in the appointment</Typography></li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: '600', mb: 2, mt: 3 }}>
            3.2 Service Providers
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            We may share information with trusted service providers who assist us in operating our platform, including:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Cloud hosting and database services (Firebase, PostgreSQL)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Authentication services (Firebase Authentication)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Payment processors (if applicable)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Analytics and monitoring services</Typography></li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7, fontStyle: 'italic' }}>
            These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: '600', mb: 2, mt: 3 }}>
            3.3 Legal Requirements
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            We may disclose your information if required by law, court order, or government regulation, or if we believe disclosure is necessary to:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Comply with legal obligations</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Protect the rights, property, or safety of Careva, our users, or others</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Investigate potential violations of our terms of service</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Respond to legal requests from law enforcement</Typography></li>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 4: Data Security */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.main }}>
            4. Data Security
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            We implement industry-standard security measures to protect your personal information:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Encryption of data in transit using SSL/TLS protocols</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Secure authentication through Firebase Authentication</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Access controls and authentication requirements for sensitive data</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Regular security assessments and updates</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Secure database storage with access restrictions</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Employee training on data protection and privacy</Typography></li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7, fontStyle: 'italic' }}>
            While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure. 
            We cannot guarantee absolute security but are committed to maintaining the highest standards of data protection.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 5: HIPAA Compliance */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.main }}>
            5. HIPAA Compliance and Health Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            Careva is designed to facilitate connections between patients and healthcare providers. We understand the importance of protecting 
            health information and are committed to compliance with applicable healthcare privacy regulations.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            <strong>Important Note:</strong> Careva operates as a platform connecting patients with caregivers. We are not a covered entity 
            under HIPAA, but we take health information privacy seriously. When you share health information through our platform:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>We implement security measures to protect health information</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>We limit access to health information to authorized personnel only</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>We do not use health information for marketing purposes without your explicit consent</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>We maintain audit logs of access to sensitive health information</Typography></li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            If you are a healthcare provider using our platform, you are responsible for ensuring compliance with HIPAA and other applicable 
            healthcare privacy laws in your interactions with patients.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 6: Your Rights */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.main }}>
            6. Your Rights and Choices
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            You have the following rights regarding your personal information:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}><strong>Access:</strong> You can access and review your personal information through your account settings</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}><strong>Update:</strong> You can update or correct your personal information at any time</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}><strong>Delete:</strong> You can request deletion of your account and personal information (subject to legal retention requirements)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}><strong>Opt-Out:</strong> You can opt out of marketing communications and certain notifications through your account settings</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}><strong>Data Portability:</strong> You can request a copy of your data in a portable format</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}><strong>Withdraw Consent:</strong> You can withdraw consent for certain data processing activities</Typography></li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            To exercise these rights, please contact us at <Link href="mailto:privacy@careva.ai">privacy@careva.ai</Link> or through your account settings.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 7: Cookies */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.main }}>
            7. Cookies and Tracking Technologies
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            We use cookies and similar tracking technologies to enhance your experience on our platform:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}><strong>Essential Cookies:</strong> Required for the platform to function properly (authentication, session management)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}><strong>Analytics Cookies:</strong> Help us understand how users interact with our platform</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}><strong>Preference Cookies:</strong> Remember your settings and preferences</Typography></li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            You can control cookie settings through your browser preferences. However, disabling certain cookies may affect the functionality of our platform.
            For more information, please see our <Link href="/cookies">Cookie Policy</Link>.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 8: Data Retention */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.main }}>
            8. Data Retention
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            We retain your personal information for as long as necessary to:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Provide our services to you</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Comply with legal obligations</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Resolve disputes and enforce our agreements</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Maintain business records as required by law</Typography></li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            When you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required 
            to retain it for legal or regulatory purposes.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 9: Children's Privacy */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.main }}>
            9. Children's Privacy
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. 
            If we become aware that we have collected information from a child under 18, we will take steps to delete that information promptly. 
            If you believe we have collected information from a child, please contact us immediately.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 10: International Users */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.main }}>
            10. International Data Transfers
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            Your information may be transferred to and processed in countries other than your country of residence. These countries may have 
            different data protection laws. By using our services, you consent to the transfer of your information to these countries. We take 
            appropriate safeguards to ensure your information receives adequate protection.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 11: Changes to Policy */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.main }}>
            11. Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory 
            reasons. We will notify you of any material changes by:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3 }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Posting the updated policy on this page</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Updating the "Last Updated" date</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Sending you an email notification (for significant changes)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.7 }}>Displaying a notice in our application</Typography></li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            Your continued use of our services after any changes indicates your acceptance of the updated Privacy Policy.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 12: Contact */}
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.primary.main }}>
            12. Contact Us
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
          </Typography>
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
              <strong>Email:</strong> <Link href="mailto:privacy@careva.ai">privacy@careva.ai</Link>
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
              <strong>Support:</strong> <Link href="/contact">Contact Support</Link>
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              <strong>Address:</strong> Careva Privacy Team
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', fontStyle: 'italic', textAlign: 'center' }}>
            By using Careva, you acknowledge that you have read and understood this Privacy Policy and agree to the collection, 
            use, and disclosure of your information as described herein.
          </Typography>
        </Box>
      </ContentSection>
    </PageLayout>
  );
};
